import { EyeSightService } from '$core/services/eye-sight.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnInit } from '@angular/core';
import {
  faChevronDown,
  faCog,
  faEyeSlash,
  faNewspaper,
  faTimes,
  faUndo,
} from '@fortawesome/free-solid-svg-icons';

enum FontTypes {
  default = 'sansSerif',
  sansSerif = 'sansSerif',
  serif = 'serif',
}

enum FontIntervals {
  default = 'standard',
  standard = 'standard',
  medium = 'medium',
  large = 'large',
}

enum GraphicMode {
  default = 'disabled',
  disabled = 'disabled',
  enabled = 'enabled',
  monochrome = 'monochrome',
}

enum ColorMode {
  default = 'black-on-white',
  blackOnWhite = 'black-on-white',
  whiteOnBlack = 'white-on-black',
  navyOnBlue = 'navy-on-blue',
  greenOnBrown = 'green-on-brown',
}

@Component({
  selector: 'app-eye-panel',
  templateUrl: './eye-panel.component.html',
  styleUrls: ['./eye-panel.component.scss'],
  animations: [
    trigger('panel', [
      transition(':enter', [
        style({ height: '0', padding: 0, opacity: 0, overflow: 'hidden' }),
        animate('0.2s', style({ height: '*', padding: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ height: '*', overflow: 'hidden' }),
        animate('0.2s', style({ height: '0', padding: 0, opacity: 0 })),
      ]),
    ]),
  ],
})
export class EyePanelComponent implements OnInit {
  public readonly faCog = faCog;
  public readonly faEyeSlash = faEyeSlash;
  public readonly faChevronDown = faChevronDown;
  public readonly faUndo = faUndo;
  public readonly faNewspaper = faNewspaper;
  public readonly faTimes = faTimes;
  public readonly FontTypes = FontTypes;
  public readonly FontIntervals = FontIntervals;
  public readonly GraphicMode = GraphicMode;
  public readonly ColorMode = ColorMode;

  public showSetting = false;
  public fontSizeDefault = 1;
  public fontSizeLevels = [1, 2, 3];
  public fontSizeTitles = [
    'FONT_SIZE_SMALL',
    'FONT_SIZE_MEDIUM',
    'FONT_SIZE_LARGE',
  ];
  public currentFontSizeLevel =
    Number(localStorage.getItem('eye-fz')) || this.fontSizeDefault;
  public currentFontType = localStorage.getItem('eye-ft') || FontTypes.default;
  public currentFontInterval =
    localStorage.getItem('eye-fi') || FontIntervals.default;
  public currentGraphicMode =
    localStorage.getItem('eye-graphic') || GraphicMode.default;
  public currentColorMode =
    localStorage.getItem('eye-color') || ColorMode.default;

  constructor(public eyeService: EyeSightService) {}

  public ngOnInit() {
    this.eyeService.status$.subscribe((e) => {
      if (e) {
        this.set();
      } else {
        this.removeClassesFromBody();
      }
    });
  }

  private set() {
    this.changeFontSize(this.currentFontSizeLevel);
    this.changeFontType(this.currentFontType);
    this.changeFontInterval(this.currentFontInterval);
    this.changeGraphicMode(this.currentGraphicMode);
    this.changeColorMode(this.currentColorMode);
  }

  private removeClassesFromBody() {
    const classes = Array.from(document.body.classList).filter((c) =>
      c.includes('eye-')
    );
    classes.forEach((c) => {
      document.body.classList.remove(c);
    });
  }

  public changeFontSize(level: number) {
    document.body.classList.remove(`eye-fz-${this.currentFontSizeLevel}`);
    document.body.classList.add(`eye-fz-${level}`);
    localStorage.setItem('eye-fz', level.toString());
    this.currentFontSizeLevel = level;
  }

  public changeFontType(type) {
    document.body.classList.remove(`eye-ft-${this.currentFontType}`);
    document.body.classList.add(`eye-ft-${type}`);
    localStorage.setItem('eye-ft', type);
    this.currentFontType = type;
  }

  public changeFontInterval(interval) {
    document.body.classList.remove(`eye-fi-${this.currentFontInterval}`);
    document.body.classList.add(`eye-fi-${interval}`);
    localStorage.setItem('eye-fi', interval);
    this.currentFontInterval = interval;
  }

  public changeGraphicMode(mode) {
    document.body.classList.remove(`eye-graphic-${this.currentGraphicMode}`);
    document.body.classList.add(`eye-graphic-${mode}`);
    localStorage.setItem('eye-graphic', mode);
    this.currentGraphicMode = mode;
  }

  public changeColorMode(mode) {
    document.body.classList.remove(`eye-color-${this.currentColorMode}`);
    document.body.classList.add(`eye-color-${mode}`);
    localStorage.setItem('eye-color', mode);
    this.currentColorMode = mode;
  }

  public setDefaults() {
    this.removeClassesFromBody();
    this.currentFontSizeLevel = this.fontSizeDefault;
    this.currentFontType = FontTypes.default;
    this.currentFontInterval = FontIntervals.default;
    this.currentGraphicMode = GraphicMode.default;
    this.currentColorMode = ColorMode.default;
    this.set();
  }

  public scrollToContent() {
    document.getElementById('shell-content').scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
}
